import * as React from "react";
import { useState } from "react";
import { format, addDays } from "date-fns";

import t from "./locales";
import Button from "../Shared/Button";
import { IPriceTable } from "./types";
import { CURRENT_CART_VERSION, DATE_FORMAT } from "./constants";
import { ICartItems, IOrderItem, IPeople, IResort } from "./types";
import { retrieveCartItems, storeCartItems } from "./helpers";
import BankeiLiftTicketModal from "./BankeiLiftTicketModal";
import i18n from "../i18n";
import { getImage } from "../utils/pathHelper";
import earlyBirdIcon from "../PriceCalculator/images/alarm-clock.svg";

// Fake API integration for new.
const syncCart = () => {
	return new Promise<void>((resolve) => {
		setTimeout(() => {
			resolve();
		}, 1000);
	});
};

interface IProps {
	itemId: number;
	itemName: string;
	people: IPeople;
	payableDateList: Date[];
	dateDuration: number;
	priceTable: IPriceTable;
	resort?: IResort;
	maxParticipants?: number;
	timeSchedule?: string;
	validate: () => boolean;
	isKanjiwari?: boolean;
	deposit: number;
	numberOfVouchers?: number;
	isCharge: boolean;
	forAxess: boolean;
	merchantId?: string;
	typeLift?: boolean;
	isEarlyBird?: boolean;
	saleDateTo?: Date;
	setProduct?: boolean;
	/** リクエスト商品など: カートに入れず決済入力（/snow/me/payment/new）へ */
	directCheckout?: boolean;
}

const SnowItemCartButton: React.FC<IProps> = (props) => {
	const {
		itemId,
		itemName,
		people,
		payableDateList,
		dateDuration,
		priceTable,
		resort,
		maxParticipants,
		timeSchedule,
		validate,
		isKanjiwari,
		deposit,
		isCharge,
		forAxess,
		merchantId,
		typeLift,
		isEarlyBird,
		saleDateTo,
		setProduct,
		directCheckout,
	} = props;
	const [isLoading, toggleLoading] = useState(false);
	const [isModalShown, setIsModalShown] = useState(false);

	const shouldShowModal = merchantId === "m-bankei" && typeLift === true;

	const primaryButtonLabel = directCheckout ? t.proceedToReservation : t.addToCart;

	const goToPaymentNew = (): void => {
		if (isLoading) {
			return;
		}
		if (!validate()) {
			return;
		}
		toggleLoading(true);
		const { adultNum, childNum, studentNum, seniorNum } = people;
		const firstDateString = format(payableDateList[0], "yyyy-MM-dd");
		const query = [
			`item_id=${itemId}`,
			`order[adult_num]=${adultNum}`,
			`order[child_num]=${childNum}`,
			`order[student_num]=${studentNum ?? 0}`,
			`order[senior_num]=${seniorNum ?? 0}`,
			`order[scheduled_date]=${firstDateString}`,
			`order[time_schedule]=${encodeURIComponent(timeSchedule || "")}`,
		];
		location.href = `/snow/me/payment/new?${query.join("&")}`;
	};

	const addToCart = async (): Promise<void> => {
		if (isLoading) {
			return;
		}
		if (!validate()) {
			return;
		}
		toggleLoading(true);
		const prevCartItems: ICartItems = retrieveCartItems(CURRENT_CART_VERSION);
		let orderItems: IOrderItem[] = prevCartItems.orderItems || [];

		orderItems = (prevCartItems.orderItems || []).concat(
			payableDateList.map((date) => {
				const checkInDate = format(date, DATE_FORMAT);
				const checkOutDate = format(
					addDays(date, dateDuration - 1),
					DATE_FORMAT,
				);
				const price = priceTable[checkInDate];
				return {
					itemId,
					itemName,
					people,
					checkInDate,
					checkOutDate,
					price,
					maxParticipants,
					resort,
					timeSchedule,
					isKanjiwari,
					forAxess,
					type: "Order",
					deposit,
					isCharge,
					setProduct,
				};
			}),
		);
		const cartItems = { ...prevCartItems, orderItems };
		await syncCart();
		storeCartItems(cartItems);
		toggleLoading(false);
	};

	const handleClick = async (event: React.MouseEvent): Promise<void> => {
		event.preventDefault();
		if (directCheckout) {
			if (shouldShowModal) {
				setIsModalShown(true);
			} else {
				goToPaymentNew();
			}
			return;
		}
		if (shouldShowModal) {
			setIsModalShown(true);
		} else {
			await addToCart();
		}
	};

	const handleModalConfirm = async (): Promise<void> => {
		if (directCheckout) {
			goToPaymentNew();
			return;
		}
		await addToCart();
	};

	return (
		<>
			{/* snow-cart-button = gtmのカートインイベント用のクラス */}
			{/* TODO: クラス以外の方法でトリガーする */}
			{isEarlyBird ? (
				<Button
					href="#"
					onClick={handleClick}
					isLoading={isLoading}
					snowItemButton={true}
					className="snow-cart-button snow-cart-button--early-bird"
				>
					<div className="snow-cart-button--early-bird-note">
						<img
							src={getImage(earlyBirdIcon)}
							alt="Early Bird"
							width={16}
							height={16}
						/>
						<div className="snow-cart-button--early-bird-text">
							{i18n.t("snow_items.show.early_bird_sale_until_html", {
								date: format(saleDateTo, "MM/dd"),
							})}
						</div>
					</div>
					<div className="d-flex align-items-center justify-content-center">
						{primaryButtonLabel()}
					</div>
				</Button>
			) : (
				<Button
					href="#"
					onClick={handleClick}
					isLoading={isLoading}
					snowItemButton={true}
					className="snow-cart-button"
				>
					{primaryButtonLabel()}
				</Button>
			)}
			{shouldShowModal && (
				<BankeiLiftTicketModal
					isShown={isModalShown}
					onClose={() => setIsModalShown(false)}
					onConfirm={handleModalConfirm}
				/>
			)}
		</>
	);
};

export default SnowItemCartButton;
